import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { EASE } from "@/App";

export default function Chapter({
  id,
  kicker,
  title,
  description,
  bullets,
  image,
  alt,
  reverse,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  return (
    <section
      ref={ref}
      data-testid={`chapter-${id}`}
      className="py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
        <motion.div
          className={`md:col-span-5 ${reverse ? "md:order-2" : ""}`}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE }}
        >
          <div className="flex items-end gap-5">
            <span className="font-display text-7xl md:text-8xl leading-[0.8] text-outline">
              {id}
            </span>
            <span className="uppercase tracking-[0.25em] text-xs text-[#D1BFAE] pb-1">
              {kicker}
            </span>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mt-8 text-[#F9F9F9]">
            {title}
          </h3>
          <p className="text-[#C4C9CE] text-base sm:text-lg leading-relaxed mt-6">
            {description}
          </p>
          <ul className="mt-8 space-y-3">
            {bullets.map((b) => (
              <li
                key={b}
                className="flex items-start gap-3 text-sm text-[#C4C9CE]"
              >
                <Check
                  size={15}
                  strokeWidth={1.5}
                  className="text-[#D1BFAE] mt-0.5 shrink-0"
                />
                {b}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          className={`md:col-span-7 ${reverse ? "md:order-1" : ""}`}
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, ease: EASE }}
        >
          <div className="relative overflow-hidden aspect-[4/3] shadow-[0_8px_32px_rgba(0,0,0,0.5)] group border border-white/5">
            <motion.img
              src={image}
              alt={alt}
              style={{ y }}
              data-testid={`chapter-${id}-image`}
              className="absolute inset-x-0 -top-[10%] w-full h-[120%] object-cover grayscale-[35%] group-hover:grayscale-0 transition-[filter] duration-700"
            />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-700 pointer-events-none" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#9aa1a7] mt-4">
            Réalisation {id} — Salle de Bain Expert
          </p>
        </motion.div>
      </div>
    </section>
  );
}
